/* ============================================================
   Email-verification wall — shown to a signed-in account that hasn't
   confirmed its email yet (new open-signups). Blocks onboarding + app
   until confirmed. The server-side half lives at the gateway
   (EMAIL_VERIFICATION_REQUIRED); this screen is the user-facing wall,
   gated client-side by RequireVerifiedEmail — default
   OFF since 2026-09-10 (owner decision: e-mail confirmation must not block
   using the product; Settings' "Confirm your email" card nudges instead),
   opt-in via localStorage 'dathent.emailVerify.wall'='1'. Server-granted Free
   publishing is exempt from this wall. Reuses the
   white-first auth surface (.auth-*). resend()/refresh() come from the
   shared lib/useEmailVerificationActions.js hook (also used by the Settings
   card) — this screen's own copy/markup is otherwise unchanged.
   ============================================================ */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';
import { useEmailVerificationActions } from '../lib/useEmailVerificationActions.js';

export default function VerifyEmailWall() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { sending, sent, checking, error, resend, refresh } = useEmailVerificationActions();

  return (
    <div className="auth-page">
      <header className="auth-topbar">
        <div className="auth-topbar-l">
          <span className="brand-mark">D</span>
          <span>Dathent</span>
        </div>
        <div className="auth-topbar-r">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-card">
        <div className="auth-mail-badge"><I.send size={22} /></div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.confirmYourEmail')}</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            {t('auth.weSentConfirmTo', 'We sent a confirmation link to')}{' '}
            <b style={{ color: 'var(--text)' }}>{user?.email}</b>.{' '}
            {t('auth.clickItToContinue', 'Click it to start your free trial and set up your brand.')}
          </p>
        </div>

        {sent && (
          <div className="auth-info" style={{ textAlign: 'left' }}>
            {t('auth.confirmResent', 'A fresh link is on its way — check your inbox (and spam).')}
          </div>
        )}
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}

        <button className="auth-btn violet block" type="button" onClick={refresh} disabled={checking}>
          {checking ? t('auth.checking', 'Checking…') : t('auth.iveConfirmed', "I've confirmed — continue")}
        </button>

        <div className="resend-line" style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
          <button type="button" className="linklike" onClick={resend} disabled={sending}>
            {sending ? t('auth.sending') : (sent ? t('auth.resendLink') : t('auth.resendConfirmation', 'Resend confirmation link'))}
          </button>
          <button type="button" className="linklike" style={{ color: 'var(--text-subtle)' }} onClick={logout}>
            {t('auth.signOut', 'Sign out')}
          </button>
        </div>
      </div>
    </div>
  );
}
